import { useState } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from '../lib/supabase';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';
import AppLogo from "../components/AppLogo";

// Route: /signup        → Client
//        /signup?type=pro → JiraniPro
//
// AUTH STRATEGY (phone + password, no OTP for now):
// 1. signUp({ phone, password, options.data }) → creates the auth user and
//    returns a live session. This requires "phone confirmations" to be
//    turned OFF in Supabase (Authentication → Providers → Phone), since no
//    SMS provider is configured yet.
// 2. profiles.upsert(...) → creates the profile row.
// LoginModal.jsx signs in with { phone, password } to match.
//
// When an SMS provider is added later, bring back OTP verification so
// phone ownership is proven at signup.
//
// ON SUCCESS:
//   role === 'pro'    → /pro-onboarding
//   role === 'client' → /

const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap";
document.head.appendChild(fontLink);

const F = "'Plus Jakarta Sans', sans-serif";

/* ── Normalise a raw phone into +254 format ── */
const normalisePhone = (p) => {
  const digits = p.replace(/[^0-9]/g, "");
  if (p.trim().startsWith("+")) return "+" + digits;
  if (digits.startsWith("254")) return "+" + digits;
  if (digits.startsWith("0")) return "+254" + digits.slice(1);
  return "+254" + digits;
};

/* ── Icons ── */
const Icon = ({ name, size = 18, color = "currentColor", strokeWidth = 1.8 }) => {
  const paths = {
    Eye:        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    EyeOff:     <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><path d="M1 1l22 22"/></>,
    ArrowRight: <><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></>,
    ArrowLeft:  <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    Building2:  <><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 0-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></>,
    Users:      <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    Shield:     <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    Briefcase:  <><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ── Field ── */
function Field({ label, type = "text", value, onChange, placeholder, error, autoFocus, inputMode, disabled }) {
  const [focused, setFocused] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";
  const { isMobile } = useBreakpoint();

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: isMobile ? 11 : 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6, fontFamily: F, letterSpacing: "-0.005em" }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type={isPassword && showPw ? "text" : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoFocus={autoFocus}
          inputMode={inputMode}
          disabled={disabled}
          style={{
            width: "100%", padding: isMobile ? "11px 14px" : "13px 16px",
            borderRadius: 12,
            border: `1.5px solid ${error ? "#ef4444" : focused ? "#16a34a" : "#e7e9ee"}`,
            fontSize: isMobile ? 13.5 : 14.5, outline: "none", fontFamily: F,
            color: "#111827", background: disabled ? "#f3f4f6" : (focused ? "#fff" : "#fafbfc"),
            boxSizing: "border-box",
            boxShadow: focused
              ? "0 0 0 4px rgba(22,163,74,0.10), inset 0 1px 2px rgba(0,0,0,0.02)"
              : "inset 0 1px 2px rgba(0,0,0,0.02)",
            transition: "border-color 0.18s, box-shadow 0.18s, background 0.18s",
            paddingRight: isPassword ? (isMobile ? 42 : 46) : (isMobile ? 14 : 16),
            minHeight: isMobile ? tokens.spacing[44] : "auto",
            letterSpacing: "-0.005em",
          }}
        />
        {isPassword && (
          <button type="button" onClick={() => setShowPw(s => !s)}
            style={{ position: "absolute", right: isMobile ? 12 : 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex", minHeight: isMobile ? tokens.spacing[44] : "auto" }}>
            <Icon name={showPw ? "EyeOff" : "Eye"} size={isMobile ? 16 : 18} strokeWidth={1.8} />
          </button>
        )}
      </div>
      {error && <p style={{ fontSize: 12, color: "#ef4444", margin: "6px 0 0 2px", fontFamily: F }}>{error}</p>}
      {isPassword && !error && <p style={{ fontSize: 11, color: "#9ca3af", margin: "5px 0 0 2px", fontFamily: F }}>At least 8 characters.</p>}
    </div>
  );
}

/* ── Error banner ── */
function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div style={{
      background: "rgba(239,68,68,0.07)", border: "1.5px solid rgba(239,68,68,0.18)",
      borderRadius: 10, padding: "11px 14px", marginBottom: 16,
    }}>
      <p style={{ fontSize: 12.5, color: "#b91c1c", margin: 0, fontFamily: F, lineHeight: 1.5 }}>{message}</p>
    </div>
  );
}

export default function SignUpPage() {
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { isMobile, isTablet } = useBreakpoint();
  const [searchParams] = useSearchParams();
  const isPro = searchParams.get("type") === "pro";

  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [phone,     setPhone]     = useState("");
  const [password,  setPassword]  = useState("");
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);

  const role     = isPro ? "pro" : "client";
  const fullName = `${firstName} ${lastName}`.trim();

  /* ── Validation ── */
  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.firstName = "Required";
    if (!lastName.trim())  e.lastName  = "Required";
    if (!phone.trim())     e.phone = "Phone number required";
    else if (phone.replace(/[^0-9]/g, "").length < 9) e.phone = "Enter a valid Kenyan phone number";
    if (password.length < 8) e.password = "At least 8 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Sign up: phone + password, no OTP ── */
  const handleSignUp = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setErrors({});

    const formattedPhone = normalisePhone(phone);

    try {
      const { data, error } = await supabase.auth.signUp({
        phone: formattedPhone,
        password,
        options: { data: { full_name: fullName, phone: formattedPhone, role } },
      });

      if (error) {
        if (error.message.toLowerCase().includes("already registered") ||
            error.message.toLowerCase().includes("already exists")) {
          setErrors({ general: "An account with this phone number already exists. Please sign in instead." });
          return;
        }
        throw error;
      }

      // No session means phone confirmation is still turned on in Supabase.
      if (!data.session || !data.user) {
        throw new Error("Sign up isn't available right now. Please try again later.");
      }

      const { error: profileError } = await supabase
        .from("profiles")
        .upsert(
          { id: data.user.id, full_name: fullName, phone: formattedPhone, role },
          { onConflict: "id" }
        );
      if (profileError) throw profileError;

      navigate(role === "pro" ? "/pro-onboarding" : "/");
    } catch (err) {
      setErrors({ general: err.message || "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  /* ── Layout ── */
  const leftPadding    = isMobile ? "24px 24px" : (isTablet ? "32px 40px" : "36px 48px");
  const rightCardWidth = isMobile ? "calc(100% - 32px)" : (isTablet ? 380 : 420);
  const rightCardMargin  = isMobile ? "16px" : "32px 48px 32px 0";
  const titleFontSize    = isMobile ? 30 : 40;
  const headingFontSize  = isMobile ? 19 : 21;
  const subheadFontSize  = isMobile ? 14 : 15;

  /* Left panel copy differs by role */
  const leftBadgeLabel = isPro ? "JiraniPro Account" : "Jirani Client Account";
  const leftHeadline   = isPro
    ? <>Earn more,<br/>on your schedule</>
    : <>Find trusted help,<br/>right in your neighbourhood</>;
  const leftSub = isPro
    ? "Join thousands of pros already earning. Set your own hours, your own rates, and grow your reputation locally."
    : "Book verified local professionals for any job around the home — starting today.";
  const leftFeatures = isPro
    ? [
        { icon: "Users",     text: "Get discovered by clients near you" },
        { icon: "Briefcase", text: "Manage bookings and earnings in one place" },
        { icon: "Shield",    text: "Jirani-verified badge builds trust fast" },
      ]
    : [
        { icon: "Users",     text: "Verified pros rated by your neighbours" },
        { icon: "Shield",    text: "Secure payments, only released on completion" },
        { icon: "Building2", text: "From plumbing to cleaning — all in one app" },
      ];

  return (
    <div style={{
      minHeight: "100vh", fontFamily: F,
      position: "relative", overflow: "hidden",
      display: "flex", alignItems: "stretch",
      flexDirection: isMobile ? "column" : "row",
      background: "linear-gradient(150deg, #07140e 0%, #0c2417 38%, #133a23 70%, #0c2417 100%)",
    }}>

      {/* Ambient glows */}
      <div style={{ position: "absolute", top: "-10%", right: "-5%", width: "55%", height: "60%", background: "radial-gradient(circle, rgba(34,197,94,0.16), transparent 70%)", filter: "blur(40px)", zIndex: 0 }} />
      <div style={{ position: "absolute", bottom: "-15%", left: "-10%", width: "50%", height: "55%", background: "radial-gradient(circle, rgba(34,197,94,0.10), transparent 70%)", filter: "blur(50px)", zIndex: 0 }} />

      {/* ── LEFT: Headline panel ── */}
      <div style={{
        position: "relative", zIndex: 2,
        flex: 1, display: "flex", flexDirection: "column",
        justifyContent: "space-between",
        padding: leftPadding,
        minHeight: isMobile ? "auto" : "100vh",
      }}>
        <span onClick={() => navigate("/")} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
          <AppLogo variant="light" />
        </span>

        <div style={{ marginTop: isMobile ? 40 : 0, marginBottom: isMobile ? 40 : 0 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 13px", borderRadius: 99, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)", marginBottom: 22 }}>
            <Icon name={isPro ? "Briefcase" : "Users"} size={13} color="#4ade80" strokeWidth={2} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#d1fae5", fontFamily: F, letterSpacing: "0.02em" }}>{leftBadgeLabel}</span>
          </div>

          <h1 style={{ fontSize: titleFontSize, fontWeight: 700, color: "#fff", margin: "0 0 16px", lineHeight: 1.15, letterSpacing: "-0.025em", maxWidth: isMobile ? "100%" : 400 }}>
            {leftHeadline}
          </h1>
          <p style={{ fontSize: subheadFontSize, color: "rgba(255,255,255,0.62)", margin: 0, maxWidth: isMobile ? "100%" : 340, lineHeight: 1.7, fontFamily: F }}>
            {leftSub}
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 30 }}>
            {leftFeatures.map((f, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "rgba(34,197,94,0.14)", border: "1px solid rgba(34,197,94,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={f.icon} size={12} color="#4ade80" strokeWidth={2} />
                </div>
                <span style={{ fontSize: 13.5, color: "rgba(255,255,255,0.75)", fontFamily: F }}>{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontSize: isMobile ? 11 : 12, color: "rgba(255,255,255,0.5)", margin: "0 0 6px", fontFamily: F }}>Already have an account?</p>
          <button onClick={() => openLogin()}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontSize: isMobile ? 14 : 15, fontWeight: 700, color: "#fff", padding: 0, fontFamily: F, minHeight: isMobile ? tokens.spacing[44] : "auto" }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
            Sign in instead
            <Icon name="ArrowRight" size={16} strokeWidth={2.5} color="#fff" />
          </button>
        </div>
      </div>

      {/* ── RIGHT: Glass card ── */}
      <div style={{
        position: "relative", zIndex: 2,
        width: rightCardWidth, flexShrink: 0,
        margin: rightCardMargin,
        background: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(28px)",
        WebkitBackdropFilter: "blur(28px)",
        borderRadius: 24,
        boxShadow: "0 32px 80px rgba(0,0,0,0.35), 0 2px 12px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.85)",
        display: "flex", flexDirection: "column",
        border: "1px solid rgba(255,255,255,0.7)",
        alignSelf: isMobile ? "center" : "stretch",
        maxHeight: isMobile ? "none" : "calc(100vh - 64px)",
        overflowY: "auto",
      }}>

        {/* ── Logo: pinned to top with generous inset ── */}
        <div style={{
          display: "flex", justifyContent: "flex-end",
          padding: isMobile ? "20px 22px 0" : "28px 32px 0",
          flexShrink: 0,
        }}>
          <AppLogo variant="dark" size="sm" />
        </div>

        {/* ── Form content: centred in remaining space ── */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
          padding: isMobile ? "20px 22px 26px" : "24px 36px 40px",
        }}>

          {/* Client / Pro toggle */}
          <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 12, padding: 4, marginBottom: 20, gap: 4 }}>
            {[{ label: "I'm a Client", value: false }, { label: "I'm a JiraniPro", value: true }].map(opt => (
              <button key={String(opt.value)}
                onClick={() => navigate(opt.value ? "/signup?type=pro" : "/signup")}
                style={{
                  flex: 1, padding: isMobile ? "8px 6px" : "10px 8px", borderRadius: 9, border: "none",
                  fontSize: isMobile ? 12 : 13, fontWeight: 600, cursor: "pointer", fontFamily: F,
                  background: isPro === opt.value ? "#fff" : "transparent",
                  color: isPro === opt.value ? "#111827" : "#6b7280",
                  boxShadow: isPro === opt.value ? "0 1px 6px rgba(0,0,0,0.10)" : "none",
                  transition: "all 0.15s",
                  minHeight: isMobile ? tokens.spacing[44] : "auto",
                }}>
                {opt.label}
              </button>
            ))}
          </div>

          <h2 style={{ fontSize: headingFontSize, fontWeight: 800, color: "#111827", margin: "0 0 4px", fontFamily: F, letterSpacing: "-0.02em" }}>
            {isPro ? "Become a JiraniPro" : "Create your account"}
          </h2>
          <p style={{ fontSize: isMobile ? 12 : 13, color: "#6b7280", margin: "0 0 20px", fontFamily: F }}>
            {isPro ? "Create your pro account to start earning." : "Enter your details to get started."}
          </p>

          <form onSubmit={handleSignUp} noValidate>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0 10px" }}>
              <Field label="First Name" value={firstName} onChange={setFirstName} placeholder="Jane"   error={errors.firstName} autoFocus />
              <Field label="Last Name"  value={lastName}  onChange={setLastName}  placeholder="Wambui" error={errors.lastName} />
            </div>
            <Field
              label="Phone Number"
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={v => { setPhone(v); setErrors(e => ({ ...e, phone: undefined, general: undefined })); }}
              placeholder="+254 700 000 000"
              error={errors.phone}
            />
            <Field
              label="Password"
              type="password"
              value={password}
              onChange={v => { setPassword(v); setErrors(e => ({ ...e, password: undefined, general: undefined })); }}
              placeholder="Create a password"
              error={errors.password}
            />

            <ErrorBanner message={errors.general} />

            <button type="submit" disabled={loading}
              style={{
                width: "100%", background: "#111827", color: "#fff",
                border: "none", borderRadius: 13, padding: isMobile ? "12px" : "14px",
                fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: loading ? "default" : "pointer",
                fontFamily: F, marginTop: 4, transition: "background 0.18s",
                opacity: loading ? 0.8 : 1,
                boxShadow: "0 8px 20px rgba(17,24,39,0.20)",
                minHeight: isMobile ? tokens.spacing[44] : "auto",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#16a34a"; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = "#111827"; }}>
              {loading ? "Creating account…" : <>Create account <Icon name="ArrowRight" size={16} color="#fff" strokeWidth={2.2} /></>}
            </button>
          </form>

          {/* Business CTA */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 18, padding: "10px 14px", borderRadius: 10, background: "rgba(22,163,74,0.06)" }}>
            <Icon name="Building2" size={14} color="#16a34a" strokeWidth={1.8} />
            <span style={{ fontSize: 12.5, color: "#4b5563", fontFamily: F }}>
              Signing up for a business?{" "}
              <span onClick={() => navigate("/signup/business")}
                style={{ color: "#16a34a", fontWeight: 700, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                Register here
              </span>
            </span>
          </div>

          <p style={{ fontSize: isMobile ? 10 : 11, color: "#9ca3af", textAlign: "center", marginTop: 16, lineHeight: 1.6, fontFamily: F }}>
            By signing up you agree to our Terms & Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}